import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import OptionIcon from '../../assets/option.png';
import { Grid, Card, CardHeader, CardContent, Button, Typography } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import Swal from 'sweetalert2';
import loadingGif from '../../assets/loading.gif';
import './OrganizationRoles.css';

const OrganizationRoles = () => {
  const [orgName, setOrgName] = useState('N/A');
  const [roles, setRoles] = useState([]);
  const [createEnabled, setCreateEnabled] = useState(false);
  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminStatusMap, setAdminStatusMap] = useState({});
  const [deleteRoleDialogOpen, setDeleteRoleDialogOpen] = useState(false);
  const [selectedDeleteRole, setSelectedDeleteRole] = useState(null);
  const [deleteRoleConfirmName, setDeleteRoleConfirmName] = useState("");


  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrgRoles = async () => {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('selectedOrg');

      if (!token || !orgId) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        // Check if the current organization is enabled
        const orgEnabledResponse = await axios.get(`${API_BASE_URL}/org/org-enabled`, {
            headers: {
                Authorization: `Bearer ${token}`,
                SelectedOrg: orgId
            }
        });
    
        const { isEnabled } = orgEnabledResponse.data;
    
        if (!isEnabled) {
            Swal.fire({
                title: 'Organization Disabled',
                text: 'This organization has been disabled by the admin. Please contact support for further assistance.',
                icon: 'error',
                confirmButtonText: 'OK'
            }).then(() => {
                navigate('/unauthorized');
            });
    
            return;
        }

        const create_enabled = await axios.get(`${API_BASE_URL}/org-roles/org-role-create`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (create_enabled.status === 403) {
          navigate('/unauthorized');
          return;
        }

        if (create_enabled.data) {
          if (!create_enabled.data.permissions.readPermission) {
            navigate('/unauthorized');
            return;
          }
          setCreateEnabled(create_enabled.data.permissions.createPermission || false);
          setUpdateEnabled(create_enabled.data.permissions.updatePermission || false);
          setDeleteEnabled(create_enabled.data.permissions.deletePermission || false);
        }

        const orgResponse = await axios.get(`${API_BASE_URL}/org/organization-settings/organization-name`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });

        if (orgResponse.status === 403) {
          navigate('/unauthorized');
          return;
        }
        setOrgName(orgResponse.data.orgName || 'N/A');

        const allRoles = await axios.get(`${API_BASE_URL}/org-roles/all-roles`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedOrg: orgId
          }
        });

        setRoles(allRoles.data);

        // Fetch and set admin statuses for all rp;es
        const statusMap = {};

        await Promise.all(allRoles.data.map(async (role) => {
          const isAdminUser = await isAdmin(role.roleName);
          statusMap[role.roleId] = isAdminUser;
        }));
        setAdminStatusMap(statusMap);

      } catch (error) {
        console.log('Failed to fetch Org Roles:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchOrgRoles();
  }, [navigate]);

  const isAdmin = async (email) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/users/is-admin`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          SelectedOrg: localStorage.getItem('selectedOrg'),
          email: email,
        },
      });

      return response.data.isAdmin === 1;
    } catch (error) {
      console.error('Failed to fetch admin status:', error);
      return false;
    }
  };

  const handleCreateRole = () => {
    navigate('/organization/roles/create');
  };


  const sortedRoles = [...roles].sort((a, b) => {
    const aIsAdmin = adminStatusMap[a.roleId];
    const bIsAdmin = adminStatusMap[b.roleId];
    if (aIsAdmin && !bIsAdmin) return -1;
    if (!aIsAdmin && bIsAdmin) return 1;
    return 0;
  });

  const selectedDeleteRoleObj = roles.find(r => r.roleId === selectedDeleteRole);

  const handleOpenDeleteRoleDialog = role => {
    setSelectedDeleteRole(role);
    setDeleteRoleConfirmName("");
    setDeleteRoleDialogOpen(true);
  };
  const handleDeleteRoleCancel = () => {
    setDeleteRoleDialogOpen(false);
    setSelectedDeleteRole(null);
    setDeleteRoleConfirmName("");
  };
  const handleDeleteRoleConfirm = async () => {
    try {
        const token = localStorage.getItem('token');
        const orgId = localStorage.getItem('selectedOrg');

        const response = await axios.post(`${API_BASE_URL}/org-roles/delete`, {
          roleId: selectedDeleteRole
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });
  
        Swal.close();
        
        if (response.status === 201) {
          Swal.fire({
            title: 'Success!',
            text: 'Role deleted successfully.',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            window.location.reload();
          });
        }
      } catch (error) {
        console.error('Failed to delete role:', error);
        Swal.close();
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete role. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        }).then(() => {
          window.location.reload();
        });
      }
    setDeleteRoleDialogOpen(false);
  };



  if (loading) {
          return (
          <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 'calc(100vh - 64px)',
              backgroundColor: '#fff',
          }}>
              <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
          </div>
          );
      }

  return (
    <>
      <div className="organizations-container">
        <div className="organizations-header">
          <h1>Organization Roles: {orgName}</h1>
          {createEnabled && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateRole}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Create Role
              </Button>
            </div>
          )}
        </div>
        <Grid container spacing={2} className="org-card-grid" justifyContent="center">
          {sortedRoles.map((role) => {
            const isAdmin = adminStatusMap[role.roleId];
            return (
              <Grid item key={role.roleId} style={{ display: 'flex', justifyContent: 'center' }}>
                <Card className="org-card" sx={{ width: '100%', width: '340px', margin: '0 auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                  <CardHeader
                    title={<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{role.roleName}</Typography>}
                    subheader={isAdmin ? 'Admin' : 'User'}
                    sx={{ background: isAdmin ? '#f5f5f5' : undefined, padding: '8px 16px' }}
                  />
                  <CardContent sx={{ padding: '8px 16px' }}>
                    <Typography variant="body2" sx={{ marginBottom: 1 }}>{role.description}</Typography>
                    <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                      {updateEnabled && !isAdmin && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => navigate(`/organization/roles/edit/${role.roleId}`)}
                        >
                          Edit
                        </Button>
                      )}
                      {deleteEnabled && !isAdmin && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleOpenDeleteRoleDialog(role.roleId, role.roleName)}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </div>
      <Dialog
        open={deleteRoleDialogOpen}
        onClose={handleDeleteRoleCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ color: "black" }}>Delete Role</DialogTitle>
        <DialogContent>
          <div style={{ marginBottom: 16 }}>
            Please type <b>{selectedDeleteRoleObj?.roleName}</b> to confirm deletion.
          </div>
          <TextField
            label={`${selectedDeleteRoleObj?.roleName}`}
            variant="outlined"
            multiline
            rows={1}
            fullWidth
            value={deleteRoleConfirmName}
            onChange={e => setDeleteRoleConfirmName(e.target.value)}
            autoFocus
            margin="normal"
            helperText={`Type the role name exactly as shown above to confirm deletion.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteRoleCancel} style={{ padding: "6px 12px" }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteRoleConfirm}
            variant="contained"
            color="error"
            style={{ padding: "6px 12px" }}
            disabled={
              !selectedDeleteRoleObj ||
              !deleteRoleConfirmName ||
              (selectedDeleteRoleObj?.roleName !== deleteRoleConfirmName)
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </>
    );
};

export default OrganizationRoles;
